import { Mail, Calendar, User, Users, Paperclip, ExternalLink, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export default function EmailDetailModal({ email, open, onClose }) {
  if (!email) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getAttachmentUrl = (attachment) => {
    if (!attachment) return null;
    if (attachment.url) {
      // If URL starts with /, prepend API base
      if (attachment.url.startsWith('/')) {
        return `${API}${attachment.url}`;
      }
      return attachment.url;
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1B4D3E]" />
            Email Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Email Header Info */}
          <div className="bg-[#F9F8F6] rounded-lg p-4 space-y-3">
            {/* To */}
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-[#66605B] mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#66605B] uppercase tracking-wide">To</p>
                <p className="text-[#1C1C1C] font-medium">
                  {email.recipient_name ? (
                    <>{email.recipient_name} &lt;{email.recipient_email}&gt;</>
                  ) : (
                    email.recipient_email
                  )}
                </p>
              </div>
            </div>

            {/* From */}
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-[#66605B] mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#66605B] uppercase tracking-wide">From</p>
                <p className="text-[#1C1C1C]">
                  {email.sender_name || 'Kawartha Luxury Properties'} &lt;{email.sender_email || 'info@kawartha-luxury-properties.com'}&gt;
                </p>
              </div>
            </div>

            {/* CC */}
            {email.cc_email && (
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-[#66605B] mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#66605B] uppercase tracking-wide">CC</p>
                  <p className="text-[#1C1C1C]">{email.cc_email}</p>
                </div>
              </div>
            )}

            {/* Date Sent */}
            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-[#66605B] mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#66605B] uppercase tracking-wide">Date Sent</p>
                <p className="text-[#1C1C1C]">{formatDate(email.sent_at)}</p>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 flex-shrink-0" />
              <Badge className={email.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {email.status === 'sent' ? 'Delivered' : 'Failed'}
              </Badge>
            </div>
          </div>

          {/* Subject */}
          <div className="border-b pb-3">
            <p className="text-xs text-[#66605B] uppercase tracking-wide mb-1">Subject</p>
            <p className="text-lg font-medium text-[#1C1C1C]">{email.subject}</p>
          </div>

          {/* Attachments */}
          {(email.attachments?.length > 0 || email.attachments_count > 0) && (
            <div className="border-b pb-3">
              <p className="text-xs text-[#66605B] uppercase tracking-wide mb-2 flex items-center gap-2">
                <Paperclip className="w-3 h-3" />
                Attachments ({email.attachments?.length || email.attachments_count})
              </p>
              {email.attachments?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {email.attachments.map((att, index) => {
                    const url = getAttachmentUrl(att);
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 bg-[#F0EBE5] rounded-lg text-sm"
                      >
                        <Paperclip className="w-4 h-4 text-[#1B4D3E]" />
                        <span className="max-w-[200px] truncate">{att.filename || att.name || `Attachment ${index + 1}`}</span>
                        {url && (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1B4D3E] hover:text-[#153E32]"
                            title="View attachment"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-[#66605B] italic">
                  {email.attachments_count} attachment(s) were sent with this email
                </p>
              )}
            </div>
          )}

          {/* Email Body */}
          <div>
            <p className="text-xs text-[#66605B] uppercase tracking-wide mb-2">Message</p>
            <div 
              className="bg-white border rounded-lg p-4 prose prose-sm max-w-none overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: email.body }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
